import * as path from 'node:path';
import type { LogFn } from '../../log.js';
import type { AcpClientHandle } from '../acp/clients/acp-client.js';
import { resolveAgentCommand } from '../acp/resolve-agent-command.js';
import { upsertCliAgentCapabilityCache } from '../../sqlite/agent-capability-cache.js';
import { withCliSqliteSync } from '../../sqlite/cli-database.js';
import {
  delayMsUnlessShutdownRequested,
  isCliImmediateShutdownRequested,
} from '../../runtime/cli-process-interrupt.js';

export type ReportAgentCapabilitiesFn = (info: { agentType: string; configOptions: unknown[] }) => void;

/**
 * Spin up a short-lived ACP session for one agent type to obtain `configOptions`.
 * Returns whether SQLite changed (hash-gated upsert).
 */
export async function probeOneAgentTypeForCapabilities(params: {
  agentType: string;
  cwd: string;
  workspaceId: string;
  log: LogFn;
  reportAgentCapabilities?: ReportAgentCapabilitiesFn;
  /** When false, only SQLite is updated (caller may send a batch to the bridge). Default true. */
  bridgeReport?: boolean;
  /** When provided, abort probing once this returns false. */
  shouldContinue?: () => boolean;
}): Promise<boolean> {
  const { agentType, cwd, workspaceId, log, reportAgentCapabilities, bridgeReport = true, shouldContinue } =
    params;
  const canContinue = () => shouldContinue?.() !== false;
  if (!canContinue()) return false;
  const resolved = resolveAgentCommand(agentType);
  if (!resolved) return false;

  let sqliteChanged = false;
  const reportedRef = { done: false };
  const tryReport = (co: unknown[] | null | undefined) => {
    if (reportedRef.done) return;
    if (!Array.isArray(co) || co.length === 0) return;
    reportedRef.done = true;
    let changed = false;
    try {
      changed = withCliSqliteSync((db) =>
        upsertCliAgentCapabilityCache(db, {
          workspaceId,
          agentType,
          configOptions: co,
        }),
      );
    } catch {
      /* still try bridge when enabled */
    }
    sqliteChanged ||= changed;
    if (bridgeReport && changed) {
      reportAgentCapabilities?.({ agentType, configOptions: co });
    }
  };

  let handle: AcpClientHandle | null = null;
  const killTimer = setTimeout(() => {
    void handle?.disconnectGracefully();
  }, 28_000);
  killTimer.unref?.();

  try {
    if (!canContinue()) return false;
    handle = await resolved.createClient({
      command: resolved.command,
      cwd: path.resolve(cwd),
      backendAgentType: agentType,
      sessionMode: 'agent',
      persistedAcpSessionId: null,
      agentConfig: null,
      getActiveConfigOptions: () => null,
      onAcpSessionEstablished: (info) => {
        tryReport(info.configOptions ?? null);
      },
      onAcpConfigOptionsUpdated: (co) => {
        tryReport(co);
      },
      onAgentSubprocessExit: () => {},
      onSessionUpdate: () => {},
    });
    if (!(await delayMsUnlessShutdownRequested(1_200)) || !canContinue()) return false;
  } catch (e) {
    log(
      `[Bridge service] Agent capability probe (${agentType}): ${e instanceof Error ? e.message : String(e)}`,
    );
  } finally {
    clearTimeout(killTimer);
    try {
      await handle?.disconnectGracefully();
    } catch {
      /* ignore */
    }
  }
  return sqliteChanged;
}
